import { ReactNode } from 'react';
import { useSelector } from 'react-redux';
import { UserRole } from '../../apis/api-client';
import Navbar from '../NavBar';
import Topbar from '../TopBar';
import ViewAsBanner from '../ViewAsBanner';
import { useNotificationRefresh } from '../../hooks/useNotificationRefresh';
import { useOwnedGroups } from '../../hooks/useOwnedGroups';
import { useViewAsLearner } from '../../utils/viewAsLearnerPref';
import { RootState } from '../../types';

interface MainLayoutProps {
  children: ReactNode;
}

export default function MainLayout({ children }: MainLayoutProps) {
  const currentUser = useSelector((state: RootState) => state.profile.user);
  const isAuthenticated = !!currentUser;
  // Learners get the sidebar-free dashboard view.
  const role = currentUser?.role;
  // "Preview as learner" toggle — an admin
  // flipping this from the topbar should ALSO drop the sidebar, since
  // the whole point is to see what a learner sees. The toggle is per-
  // tab and the topbar's "Return to admin" button reverses it (which
  // brings the sidebar back to the prior workspace-admin view).
  const viewingAsLearner = useViewAsLearner();
  const isLearner = role === UserRole.User || viewingAsLearner;
  // A learner who OWNS a group (the "group admin") is the one exception to
  // the sidebar-free learner shell: they get a sidebar with just the Groups
  // entry so they can reach their group's roster + reporting. Keyed off the
  // real role, so an admin previewing-as-learner is unaffected.
  const { isGroupAdmin } = useOwnedGroups();
  // Sidebar is shown for authenticated non-learners, plus learner group
  // admins. Other learners get a focused dashboard surface — their nav
  // happens through the topbar (avatar menu, notifications).
  const showSidebar = isAuthenticated && (!isLearner || isGroupAdmin);

  // Refresh unread count on navigation (60s dedupe) - only for authenticated users
  useNotificationRefresh();

  return (
    <div className={`flex h-screen bg-background`} style={{ flexDirection: 'row', display: 'flex' }}>
      {showSidebar && <Navbar />}
      <div className="flex flex-col flex-1 overflow-hidden" style={{ width: '100%' }}>
        {/* Amber preview banner — renders only when a super/account
            admin is viewing-as a lower role. Sticky above the topbar. */}
        {isAuthenticated && <ViewAsBanner />}
        <Topbar isAuthenticated={isAuthenticated} sidebarOffset={showSidebar} />
        <main className="flex-1 overflow-y-auto p-4 md:p-6 lg:p-8">
          {children}
        </main>
      </div>
    </div>
  );
}
